<template>
  <div class="p-6">
    <div class="mb-4 rounded-xl bg-white px-4 py-3 shadow dark:bg-gray-800 dark:shadow-gray-900">
      <div class="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div class="min-w-0 flex-1">
          <div class="flex flex-wrap justify-start gap-4">
            <button
              v-for="category in categories"
              :key="category.id"
              type="button"
              class="relative whitespace-nowrap rounded-md px-3 py-2 text-center text-sm font-bold transition-colors duration-200"
              :class="selectedCategoryId === category.id
                ? 'bg-indigo-200 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-100'
                : 'text-gray-800 hover:bg-indigo-200 dark:text-gray-200 dark:hover:bg-indigo-900'"
              @click="handleCategoryChange(category.id)"
            >
              {{ category.displayName }}
            </button>
          </div>
        </div>

        <input
          v-model.trim="keyword"
          type="text"
          placeholder="按试卷标题、书籍名或 ID 搜索"
          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 xl:w-[22rem] xl:flex-none"
        />
      </div>
    </div>

    <div class="mb-4 flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
      <span>共 {{ papers.length }} 套试卷，当前显示 {{ filteredPapers.length }} 套</span>
    </div>

    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
    </div>

    <div v-else class="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900">
      <PaperList :papers="filteredPapers" @edit="handleEdit" @edit-content="handleEditContent" />
    </div>

    <div v-if="showEditModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-70 overflow-y-auto h-full w-full z-50"
      @click.self="closeEditModal">
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">编辑试卷</h3>
          <button @click="closeEditModal" class="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400">
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form @submit.prevent="handleSubmit" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">试卷 ID</label>
            <input type="text" :value="editForm.id" disabled
              class="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">试卷标题 <span class="text-red-500">*</span></label>
            <input type="text" v-model="editForm.title" required
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">所属书籍 <span class="text-red-500">*</span></label>
            <select v-model.number="editForm.bookId" required
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600">
              <option value="" disabled>请选择书籍</option>
              <option v-for="book in books" :key="book.id" :value="book.id">
                {{ getBookLabel(book) }}
              </option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">考试日期 <span class="text-red-500">*</span></label>
            <input type="date" v-model="editForm.testDate" required
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">试卷类型 <span class="text-red-500">*</span></label>
            <select v-model="editForm.type" required
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600">
              <option value="academic">Academic</option>
              <option value="general">General</option>
            </select>
          </div>

          <div>
            <label class="flex items-center cursor-pointer">
              <input type="checkbox" v-model="editForm.isPremium"
                class="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500 focus:ring-2 dark:bg-gray-700 dark:border-gray-600" />
              <span class="ml-2 text-sm font-medium text-gray-900 dark:text-gray-100">付费试卷</span>
            </label>
          </div>

          <div class="flex justify-end gap-3 pt-4">
            <button type="button" @click="closeEditModal"
              class="cancel-btn text-sm font-medium">
              取消
            </button>
            <button type="submit" :disabled="submitting"
              class="primary-btn text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed">
              {{ submitting ? '保存中...' : '保存' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="errorMessage"
      class="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg shadow-lg"
      role="alert">
      <div class="flex items-center gap-2">
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clip-rule="evenodd" />
        </svg>
        <span>{{ errorMessage }}</span>
        <button @click="errorMessage = ''" class="ml-2 text-red-700 hover:text-red-900">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd" />
          </svg>
        </button>
      </div>
    </div>

    <div v-if="successMessage"
      class="fixed bottom-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg shadow-lg"
      role="alert">
      <div class="flex items-center gap-2">
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clip-rule="evenodd" />
        </svg>
        <span>{{ successMessage }}</span>
        <button @click="successMessage = ''" class="ml-2 text-green-700 hover:text-green-900">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import PaperList from '@/components/mock/PaperList.vue'
import { getAllPapersAPI, updatePaperAPI } from '@/api/mock/paper'
import { getAllBooksAPI } from '@/api/mock/book'
import { getAllCategoriesAPI } from '@/api/mock/category'
import type { Paper, PaperType, UpdatePaperRequest } from '@/types/mock/paper'
import type { Book } from '@/types/mock/book'
import type { Category } from '@/types/mock/category'
import { HttpStatus } from '@/types/axios/httpStatus'

interface PaperEditForm {
  id: number
  bookId: number | null
  title: string
  testDate: string
  type: PaperType
  isPremium: boolean
}

/**
 * 试卷列表
 */
const papers = ref<Paper[]>([])

/**
 * 书籍列表
 */
const books = ref<Book[]>([])
const categories = ref<Category[]>([])
const router = useRouter()

const keyword = ref('')
const selectedCategoryId = ref<number | null>(null)
const loading = ref(false)
const errorMessage = ref('')
const successMessage = ref('')
const showEditModal = ref(false)
const submitting = ref(false)

const createEmptyEditForm = (): PaperEditForm => ({
  id: 0,
  bookId: null,
  title: '',
  testDate: '',
  type: 'academic',
  isPremium: false
})

const editForm = ref<PaperEditForm>(createEmptyEditForm())

const filteredPapers = computed(() => {
  const normalizedKeyword = keyword.value.trim().toLowerCase()

  return papers.value.filter((paper) => {
    if (!normalizedKeyword) {
      return true
    }

    const searchableText = [
      paper.title,
      paper.book?.displayName || '',
      paper.book?.name || '',
      String(paper.id),
    ]
      .join(' ')
      .toLowerCase()

    return searchableText.includes(normalizedKeyword)
  })
})

const getBookLabel = (book: Pick<Book, 'displayName' | 'name'>) => {
  return book.displayName || book.name
}

/**
 * 加载所有试卷
 */
const loadPapers = async (categoryId?: number) => {
  loading.value = true
  errorMessage.value = ''
  try {
    const response = await getAllPapersAPI(categoryId)
    if (response.status !== HttpStatus.HTTP_200_OK || !response.data) {
      papers.value = []
      errorMessage.value = response.message || '加载试卷失败，请刷新页面重试'
      return
    }

    papers.value = response.data
  } catch (error) {
    console.error('加载试卷失败:', error)
    errorMessage.value = '加载试卷失败，请刷新页面重试'
  } finally {
    loading.value = false
  }
}

/**
 * 加载所有书籍
 */
const loadBooks = async () => {
  try {
    const response = await getAllBooksAPI()
    if (response.status !== HttpStatus.HTTP_200_OK || !response.data) {
      errorMessage.value = response.message || '加载书籍列表失败，请刷新页面重试'
      books.value = []
      return
    }

    books.value = response.data
  } catch (error) {
    console.error('加载书籍列表失败:', error)
    errorMessage.value = '加载书籍列表失败，请刷新页面重试'
  }
}

const loadCategories = async () => {
  try {
    const response = await getAllCategoriesAPI()
    if (response.status !== HttpStatus.HTTP_200_OK || !response.data) {
      errorMessage.value = response.message || '加载分类失败，请刷新页面重试'
      categories.value = []
      return
    }

    categories.value = response.data.filter((category) => category.isActive)
    if (categories.value.length > 0 && selectedCategoryId.value === null) {
      selectedCategoryId.value = categories.value[0].id
    }
  } catch (error) {
    console.error('加载分类失败:', error)
    errorMessage.value = '加载分类失败，请刷新页面重试'
  }
}

const handleCategoryChange = async (categoryId: number) => {
  if (selectedCategoryId.value === categoryId) {
    return
  }

  selectedCategoryId.value = categoryId
  await loadPapers(categoryId)
}

/**
 * 处理编辑按钮点击
 *
  * @param paper 试卷对象
 */
const handleEdit = (paper: Paper) => {
  editForm.value = {
    id: paper.id,
    bookId: paper.bookId,
    title: paper.title,
    testDate: paper.testDate,
    type: paper.type,
    isPremium: paper.isPremium
  }
  showEditModal.value = true
}

const handleEditContent = (paper: Paper) => {
  const targetUrl = router.resolve({
    name: 'mock-paper-editor',
    params: { paperId: paper.id },
  }).href

  const openedWindow = window.open(targetUrl, '_blank')
  if (openedWindow) {
    openedWindow.opener = null
  } else {
    router.push(targetUrl)
  }
}

const closeEditModal = () => {
  showEditModal.value = false
  editForm.value = createEmptyEditForm()
}

const handleSubmit = async () => {
  const normalizedTitle = editForm.value.title.trim()

  if (!editForm.value.bookId) {
    errorMessage.value = '请选择所属书籍'
    return
  }

  if (!normalizedTitle) {
    errorMessage.value = '试卷标题不能为空'
    return
  }

  submitting.value = true
  errorMessage.value = ''

  try {
    const payload: UpdatePaperRequest = {
      bookId: editForm.value.bookId,
      title: normalizedTitle,
      testDate: editForm.value.testDate,
      type: editForm.value.type,
      isPremium: editForm.value.isPremium
    }
    const response = await updatePaperAPI(editForm.value.id, payload)

    if (response.status !== HttpStatus.HTTP_200_OK || !response.data) {
      errorMessage.value = response.message || `更新试卷"${normalizedTitle}"失败，请重试`
      return
    }

    papers.value = papers.value.map((paper) =>
      paper.id === response.data.id ? response.data : paper
    )

    successMessage.value = `试卷"${response.data.title}"更新成功`
    setTimeout(() => {
      successMessage.value = ''
    }, 3000)

    closeEditModal()
  } catch (error) {
    console.error('更新试卷失败:', error)
    errorMessage.value = `更新试卷"${normalizedTitle}"失败，请重试`
  } finally {
    submitting.value = false
  }
}

onMounted(() => {
  void (async () => {
    await Promise.all([loadBooks(), loadCategories()])
    if (selectedCategoryId.value !== null) {
      await loadPapers(selectedCategoryId.value)
    } else {
      await loadPapers()
    }
  })()
})
</script>
